import React, { useRef, useState, useCallback } from "react";
import { MoveHorizontal } from "lucide-react";

// Accessible drag-to-compare slider. Original on the left, enhanced on the right.
export default function BeforeAfterSlider({ before, after, beforeAlt = "Original", afterAlt = "Enhanced" }) {
  const [pos, setPos] = useState(50);
  const ref = useRef(null);
  const dragging = useRef(false);

  const updateFromClientX = useCallback((clientX) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    let p = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(0, Math.min(100, p)));
  }, []);

  const onPointerDown = (e) => {
    dragging.current = true;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    updateFromClientX(e.clientX);
  };
  const onPointerMove = (e) => {
    if (dragging.current) updateFromClientX(e.clientX);
  };
  const onPointerUp = () => { dragging.current = false; };

  const onKeyDown = (e) => {
    if (e.key === "ArrowLeft") { setPos((p) => Math.max(0, p - 4)); e.preventDefault(); }
    if (e.key === "ArrowRight") { setPos((p) => Math.min(100, p + 4)); e.preventDefault(); }
    if (e.key === "Home") { setPos(0); e.preventDefault(); }
    if (e.key === "End") { setPos(100); e.preventDefault(); }
  };

  return (
    <div
      ref={ref}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerLeave={onPointerUp}
      className="relative aspect-[3/2] w-full cursor-ew-resize select-none overflow-hidden rounded-2xl shadow-xl [touch-action:none]"
      data-testid="before-after-slider"
    >
      {/* Base: enhanced (right side) */}
      <img src={after} alt={afterAlt} draggable={false} className="pointer-events-none absolute inset-0 h-full w-full object-cover" />
      {/* Overlay: original (left side), clipped to pos% */}
      <img
        src={before}
        alt={beforeAlt}
        draggable={false}
        className="pointer-events-none absolute inset-0 h-full w-full object-cover"
        style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
      />

      {/* Corner labels */}
      <span className="pointer-events-none absolute left-3 top-3 rounded-full bg-[#16323f]/80 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-white">
        Original
      </span>
      <span className="pointer-events-none absolute right-3 top-3 rounded-full bg-[#ec008c]/90 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-white">
        Enhanced
      </span>

      {/* Divider line */}
      <div className="pointer-events-none absolute inset-y-0 w-[3px] -translate-x-1/2 bg-white shadow-[0_0_6px_rgba(0,0,0,0.3)]" style={{ left: `${pos}%` }} />

      {/* Draggable handle */}
      <button
        type="button"
        role="slider"
        aria-label="Drag to compare original and enhanced photo"
        aria-orientation="horizontal"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(pos)}
        onKeyDown={onKeyDown}
        data-testid="before-after-handle"
        className="absolute top-1/2 grid h-12 w-12 -translate-x-1/2 -translate-y-1/2 cursor-ew-resize place-items-center rounded-full bg-white text-[#16323f] shadow-lg transition-transform hover:scale-105 focus:outline-none focus:ring-4 focus:ring-[#ec008c]/40"
        style={{ left: `${pos}%` }}
      >
        <MoveHorizontal size={20} strokeWidth={2.2} />
      </button>
    </div>
  );
}
